"use client";

import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useAuth } from "@/contexts/AuthContext";
import { Avatar } from "@/components/common/Avatar";
import { useState, useEffect } from "react";
import { getNavItems } from "./navData";
import { NotesPanel } from "./NotesPanel";
import { PhoneScreenPanel } from "./PhoneScreenPanel";
import { CommandPanel } from "./CommandPanel";

const API_BASE = process.env.NEXT_PUBLIC_API_URL || "";

export function Sidebar() {
  const pathname = usePathname();
  const router = useRouter();
  const { user, token, logout, refreshUser } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [showAvatarInput, setShowAvatarInput] = useState(false);
  const [avatarPrompt, setAvatarPrompt] = useState("");
  const [hasActiveTasks, setHasActiveTasks] = useState(false);
  const [runningPluginsCount, setRunningPluginsCount] = useState(0);
  const [runningProjectsCount, setRunningProjectsCount] = useState(0);
  const [showNotesPanel, setShowNotesPanel] = useState(false);
  const [showPhoneScreen, setShowPhoneScreen] = useState(false);
  const [showCommandPanel, setShowCommandPanel] = useState(false);

  const isAdmin = user?.role === "admin";

  // 定期检查是否有活跃的定时任务（pending 或 running）
  useEffect(() => {
    if (!token) return;
    const checkActiveTasks = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/scheduled-tasks`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          const statusCounts = data.status_counts || {};
          const activeCount = (statusCounts.pending || 0) + (statusCounts.running || 0);
          setHasActiveTasks(activeCount > 0);
        } else {
          setHasActiveTasks(false);
        }
      } catch {
        setHasActiveTasks(false);
      }
    };
    checkActiveTasks();
    const interval = setInterval(checkActiveTasks, 15000); // 15秒轮询一次，降低服务器压力
    return () => clearInterval(interval);
  }, [token]);

  // 定期检查运行中的插件数量
  useEffect(() => {
    if (!token) return;
    const checkRunningPlugins = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/plugins`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          const plugins = data.data || [];
          const runningCount = plugins.filter((p: { running?: boolean }) => p.running).length;
          setRunningPluginsCount(runningCount);
        } else {
          setRunningPluginsCount(0);
        }
      } catch {
        setRunningPluginsCount(0);
      }
    };
    checkRunningPlugins();
    const interval = setInterval(checkRunningPlugins, 15000); // 15秒轮询一次
    return () => clearInterval(interval);
  }, [token]);

  // 定期检查运行中的项目数量
  useEffect(() => {
    if (!token) return;
    const checkRunningProjects = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/projects/running`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          const runningIds = data.running_project_ids || [];
          setRunningProjectsCount(runningIds.length);
        } else {
          setRunningProjectsCount(0);
        }
      } catch {
        setRunningProjectsCount(0);
      }
    };
    checkRunningProjects();
    const interval = setInterval(checkRunningProjects, 15000); // 15秒轮询一次
    return () => clearInterval(interval);
  }, [token]);

  const handleGenerateAvatar = async () => {
    if (!user || !token) return;

    setGenerating(true);
    try {
      const res = await fetch(`${API_BASE}/api/users/${user.id}/avatar/generate`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ prompt: avatarPrompt || undefined })
      });

      if (res.ok) {
        await refreshUser();
        setShowAvatarInput(false);
        setAvatarPrompt("");
      } else {
        const data = await res.json();
        alert(data.detail || "生成失败");
      }
    } catch (error) {
      alert("生成失败");
    } finally {
      setGenerating(false);
    }
  };

  // 构建导航项列表
  const navItems = getNavItems(isAdmin);

  const handleLogout = async () => {
    await logout();
    router.push("/login");
  };

  return (
    <div className="w-16 h-screen bg-[#1a1a1a] border-r border-[#2a2a2a] flex flex-col items-center py-4 z-30 relative">
      {/* Logo - 缺角圆图标 */}
      <div className="w-10 h-10 mb-4 flex items-center justify-center cursor-pointer">
        <svg
          className="w-8 h-8 logo-spin"
          viewBox="0 0 100 100"
          style={{
            filter: "drop-shadow(0 0 8px rgba(249, 115, 22, 0.6))"
          }}
        >
          <defs>
            <linearGradient id="zenGradOrange" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style={{ stopColor: "#f97316", stopOpacity: 1 }} />
              <stop offset="100%" style={{ stopColor: "#eab308", stopOpacity: 1 }} />
            </linearGradient>
            <filter id="glowOrange">
              <feGaussianBlur stdDeviation="2.5" result="coloredBlur"/>
              <feMerge>
                <feMergeNode in="coloredBlur"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
          </defs>
          {/* 主圆环 - 上方断口 */}
          <path
            d="M 75 50 A 25 25 0 1 1 50 25 L 50 35 A 15 15 0 1 0 65 50 Z"
            fill="url(#zenGradOrange)"
            filter="url(#glowOrange)"
            opacity="0.95"
          />
          {/* 内圈辉光 */}
          <circle
            cx="50"
            cy="50"
            r="20"
            fill="none"
            stroke="url(#zenGradOrange)"
            strokeWidth="0.5"
            opacity="0.3"
          >
            <animate attributeName="opacity" values="0.3;0.6;0.3" dur="3s" repeatCount="indefinite"/>
          </circle>
        </svg>
      </div>

      {/* Navigation */}
      <div className="flex-1">
        {navItems.map((item) => {
          const isActive = pathname === item.href || pathname.startsWith(item.href + "/");
          return (
            <Link
              key={item.href}
              href={item.href}
              className={`w-10 h-10 rounded-lg flex items-center justify-center mb-2 transition-colors relative ${
                isActive
                  ? "bg-[#3a3a3a] text-[#e5e5e5]"
                  : "text-[#808080] hover:bg-[#2a2a2a] hover:text-[#e5e5e5]"
              }`}
              title={item.label}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d={item.icon}
                />
              </svg>
              {/* 活跃任务指示灯 - 有 pending/running 任务时显示 */}
              {item.hasHealthIndicator && hasActiveTasks && (
                <span
                  className="absolute bottom-1 right-1 w-2 h-2 rounded-full animate-pulse"
                  style={{
                    backgroundColor: "#22c55e",
                    boxShadow: "0 0 6px 2px rgba(34, 197, 94, 0.6)"
                  }}
                />
              )}
              {/* 运行中插件数量徽章 - 紫色风格 */}
              {item.hasPluginsBadge && runningPluginsCount > 0 && (
                <span
                  className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 text-[10px] font-medium rounded-full flex items-center justify-center"
                  style={{
                    backgroundColor: "#9333ea",
                    color: "#ffffff",
                    boxShadow: "0 0 6px 2px rgba(147, 51, 234, 0.5)"
                  }}
                >
                  {runningPluginsCount > 99 ? "99+" : runningPluginsCount}
                </span>
              )}
              {/* 运行中项目数量徽章 - 橙色风格 */}
              {item.hasProjectsBadge && runningProjectsCount > 0 && (
                <span
                  className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 text-[10px] font-medium rounded-full flex items-center justify-center"
                  style={{
                    backgroundColor: "#f97316",
                    color: "#ffffff",
                    boxShadow: "0 0 6px 2px rgba(249, 115, 22, 0.5)"
                  }}
                >
                  {runningProjectsCount > 99 ? "99+" : runningProjectsCount}
                </span>
              )}
            </Link>
          );
        })}
      </div>

      {/* Phone Screen Button */}
      <button
        onClick={() => setShowPhoneScreen(!showPhoneScreen)}
        className={`w-10 h-10 rounded-lg flex items-center justify-center mb-2 transition-colors ${
          showPhoneScreen
            ? "bg-[#3a3a3a] text-[#10b981]"
            : "text-[#808080] hover:bg-[#2a2a2a] hover:text-[#e5e5e5]"
        }`}
        title="手机屏幕"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-5 w-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
          />
        </svg>
      </button>

      {/* Phone Screen Panel */}
      {showPhoneScreen && (
        <PhoneScreenPanel onClose={() => setShowPhoneScreen(false)} />
      )}

      {/* Notes Button */}
      <button
        onClick={() => setShowNotesPanel(!showNotesPanel)}
        className={`w-10 h-10 rounded-lg flex items-center justify-center mb-2 transition-colors ${
          showNotesPanel
            ? "bg-[#3a3a3a] text-[#f97316]"
            : "text-[#808080] hover:bg-[#2a2a2a] hover:text-[#e5e5e5]"
        }`}
        title="快速笔记"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-5 w-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
          />
        </svg>
      </button>

      {/* Notes Panel */}
      {showNotesPanel && (
        <NotesPanel onClose={() => setShowNotesPanel(false)} />
      )}

      {/* Command Panel */}
      {showCommandPanel && (
        <CommandPanel onClose={() => setShowCommandPanel(false)} />
      )}

      {/* User Avatar & Menu */}
      {user && (
        <div className="relative">
          <button
            onClick={() => setShowUserMenu(!showUserMenu)}
            className="w-10 h-10 rounded-lg flex items-center justify-center hover:bg-[#2a2a2a] transition-colors"
            title={user.display_name || user.username}
          >
            <Avatar
              src={user.avatar}
              name={user.display_name || user.username}
              size="md"
              style={{ backgroundColor: "#4a4a4a", color: "#ffffff" }}
            />
          </button>

          {/* Dropdown Menu */}
          {showUserMenu && (
            <>
              {/* Backdrop */}
              <div
                className="fixed inset-0 z-40"
                onClick={() => setShowUserMenu(false)}
              />
              {/* Menu */}
              <div
                className="absolute bottom-full left-0 mb-2 w-48 rounded-lg shadow-lg z-50 py-1"
                style={{ backgroundColor: "#2a2a2a", border: "1px solid #3a3a3a" }}
              >
                {/* User Info */}
                <div className="px-3 py-2 border-b border-[#3a3a3a]">
                  <div className="text-sm text-[#e5e5e5] font-medium truncate">
                    {user.display_name || user.username}
                  </div>
                  <div className="text-xs text-[#808080] truncate">
                    @{user.username}
                  </div>
                  <div className="text-xs text-[#808080] mt-1">
                    {user.role === "admin" ? "管理员" : user.role === "member" ? "成员" : "访客"}
                  </div>
                </div>

                {/* Menu Items */}
                {!showAvatarInput ? (
                  <button
                    onClick={() => setShowAvatarInput(true)}
                    className="w-full text-left px-3 py-2 text-sm text-[#e5e5e5] hover:bg-[#3a3a3a] transition-colors flex items-center gap-2"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                      />
                    </svg>
                    AI 生成头像
                  </button>
                ) : (
                  <div className="px-3 py-2 space-y-2">
                    <input
                      type="text"
                      value={avatarPrompt}
                      onChange={(e) => setAvatarPrompt(e.target.value)}
                      placeholder="描述你想要的头像风格..."
                      className="w-full px-2 py-1 text-xs rounded bg-[#1a1a1a] text-[#e5e5e5] border border-[#3a3a3a] outline-none focus:border-[#4a4a4a]"
                      disabled={generating}
                    />
                    <div className="flex gap-1">
                      <button
                        onClick={handleGenerateAvatar}
                        disabled={generating}
                        className="flex-1 px-2 py-1 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded disabled:opacity-50"
                      >
                        {generating ? "生成中..." : "生成"}
                      </button>
                      <button
                        onClick={() => { setShowAvatarInput(false); setAvatarPrompt(""); }}
                        disabled={generating}
                        className="px-2 py-1 text-xs bg-[#3a3a3a] hover:bg-[#4a4a4a] text-[#e5e5e5] rounded disabled:opacity-50"
                      >
                        取消
                      </button>
                    </div>
                  </div>
                )}
                {/* 执行命令 - 仅管理员可见 */}
                {isAdmin && (
                  <button
                    onClick={() => {
                      setShowUserMenu(false);
                      setShowCommandPanel(true);
                    }}
                    className="w-full text-left px-3 py-2 text-sm text-[#e5e5e5] hover:bg-[#3a3a3a] transition-colors flex items-center gap-2"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                    执行命令
                  </button>
                )}
                <button
                  onClick={handleLogout}
                  className="w-full text-left px-3 py-2 text-sm text-[#e5e5e5] hover:bg-[#3a3a3a] transition-colors flex items-center gap-2"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                    />
                  </svg>
                  退出登录
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
